import {
  hasQrCodeLink,
  PostQrCode,
  PostQrCodeBox,
} from "@package/ui-apollo-components";
import { PostQrCodeLocationType } from "@package/ui-gql/index-data-access";
import { useSelectorWall } from "@package/ui-redux";
import { type SliceWallScreenState } from "@package/ui-wall-engine";
import { type PropsWithChildren } from "react";

type FlyoutQrCodeProps = {
  screen: SliceWallScreenState;
};

function FlyoutQrCodePositionedContainer({
  children,
  screen,
}: PropsWithChildren<FlyoutQrCodeProps>) {
  const post = screen.flyout?.post;
  const percent = post?.qrCodeLocationPercent;
  const isPercentPositioned = !!percent?.width && !!percent?.height;

  // Percentages of the flyout box, written by the CMS visual editor and
  // mirrored into `qrCodeLocation` (pixels) for older wall bundles. Unlike
  // `custom` (raw pixels), this survives any flyout box size.
  if (isPercentPositioned) {
    return (
      <div
        className="absolute"
        style={{
          left: `${percent?.x ?? 0}%`,
          top: `${percent?.y ?? 0}%`,
          width: `${percent?.width ?? 0}%`,
          height: `${percent?.height ?? 0}%`,
        }}
      >
        {children}
      </div>
    );
  }

  const location = post?.qrCodeLocation;
  const type = location?.type ?? PostQrCodeLocationType.Bottom;

  if (type === PostQrCodeLocationType.Top) {
    return (
      <div
        className="absolute top-1/3 left-1/2"
        style={{ transform: "translate(-50%, -33.3%)" }}
      >
        {children}
      </div>
    );
  }

  if (type === PostQrCodeLocationType.Bottom) {
    return (
      <div
        className="absolute bottom-1/3 left-1/2"
        style={{ transform: "translate(-50%, 33.3%)" }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className="absolute"
      style={{
        left: location?.x ?? undefined,
        top: location?.y ?? undefined,
        width: location?.width ?? undefined,
        height: location?.height ?? undefined,
      }}
    >
      {children}
    </div>
  );
}

export function FlyoutQrCode({ screen }: FlyoutQrCodeProps) {
  const location = useSelectorWall((state) => state.wall.location);
  const profile = useSelectorWall((state) => state.wall.profile);
  const services = useSelectorWall((state) => state.wall.services);
  const serviceFlyout = services?.find(
    (s) => s.id === screen?.flyout?.post?.flyoutType,
  );
  const isEmbedded =
    !!serviceFlyout?.options?.cms?.post?.qrCodeEmbeddedInFlyout;
  const post = screen?.flyout?.post;
  const percent = post?.qrCodeLocationPercent;
  const isCanvasPosition = !!percent?.width && !!percent?.height;

  if (isEmbedded || !hasQrCodeLink(location, profile, post)) {
    return null;
  }

  return (
    <FlyoutQrCodePositionedContainer screen={screen}>
      <PostQrCodeBox
        text={post?.qrCodeText}
        style={post?.qrCodeStyle}
        fill={isCanvasPosition}
      >
        <PostQrCode
          location={location}
          profile={profile}
          post={post}
          fgColor={post?.qrCodeStyle?.qrColor ?? undefined}
          bgColor={post?.qrCodeStyle?.qrBackgroundColor ?? undefined}
        />
      </PostQrCodeBox>
    </FlyoutQrCodePositionedContainer>
  );
}
